import { Navigate, useLocation, useNavigate, useRoutes } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import DashboardLayout from './layouts/dashboard';
import SimpleLayout from './layouts/simple';
import LoginPage from './pages/LoginPage';
import Page404 from './pages/Page404';
import { useSelector } from 'react-redux';
import { useEffect } from 'react';
import { isAdminRole, isStaffRole } from './utils/roles';

const BlogPage = lazy(() => import('./pages/BlogPage'));
const UserPage = lazy(() => import('./pages/UserPage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const DashboardAppPage = lazy(() => import('./pages/DashboardAppPage'));
const SettingPage = lazy(() => import('./pages/SettingPage'));
const SupportsPage = lazy(() => import('./pages/SupportPage'));
const BirthdayPage = lazy(() => import('./pages/BirthdayPage'));
const UserActivityPage = lazy(() => import('./pages/UserActivityPage'));

const RouteFallback = () => (
  <div style={{
    height: '100vh', width: '100vw', display: 'flex', justifyContent: 'center', alignItems: 'center',
  }}>
    <img src='assets/loader1.gif' style={{ height: '80px' }} alt="loading" />
  </div>
);

const PATH_ACCESS_MAP = [
  { prefix: '/dashboard/activity', section: 'users' },
  { prefix: '/dashboard/app', section: 'dashboard' },
  { prefix: '/dashboard/user', section: 'users' },
  { prefix: '/dashboard/products', section: 'teams' },
  { prefix: '/dashboard/blog', section: 'pages' },
  { prefix: '/dashboard/settings', section: 'settings' },
  { prefix: '/dashboard/supports', section: 'supports' },
  { prefix: '/dashboard/birth', section: 'dashboard' },
];

function DefaultDashboardRedirect() {
  const auth = useSelector(state => state.auth)
  const role = auth?.user?.role

  if (isStaffRole(role) || isAdminRole(role)) {
    if (isStaffRole(role) && auth?.user?.accessLevel) {
      const allowed = auth?.user?.accessLevel?.dashboard?.read
      if (!allowed) {
        const fallback = PATH_ACCESS_MAP.find(p => auth?.user?.accessLevel?.[p.section]?.read)
        return <Navigate to={fallback ? fallback.prefix : "/dashboard/products"} replace />
      }
    }
    return <Navigate to="/dashboard/app" replace />
  }

  return <Navigate to="/dashboard/products" replace />
}

export default function Router() {

  const navigate = useNavigate()
  const location = useLocation()
  const auth = useSelector(state => state.auth)
  useEffect(() => {
    const token = localStorage.getItem('@token')
    if (auth?.authorize || token) {
      if (auth?.user?.role != undefined) {
        if (isStaffRole(auth?.user?.role) || isAdminRole(auth?.user?.role)) {
          if (isStaffRole(auth?.user?.role) && auth?.user?.accessLevel) {
            const matched = PATH_ACCESS_MAP.find(p => location.pathname.startsWith(p.prefix))
            const allowed = matched ? !!auth?.user?.accessLevel?.[matched.section]?.read : true
            if (matched && !allowed) {
              const fallback = PATH_ACCESS_MAP.find(p => auth?.user?.accessLevel?.[p.section]?.read)
              navigate(fallback ? fallback.prefix : "/dashboard/products", { replace: true })
            }
          }
        } else {
          navigate("/dashboard/products", { replace: true })
        }
      }
    } else {
      navigate("/login", { replace: true })
    }
  }, [auth, location.pathname])
  const routes = useRoutes([
    {
      path: 'login',
      element: <LoginPage />,
    },
    {
      path: '/dashboard',
      element: <DashboardLayout />,
      children: [
        { element: <DefaultDashboardRedirect />, index: true },
        { path: 'app', element: <DashboardAppPage /> },
        { path: 'user', element: <UserPage /> },
        { path: 'products', element: <ProductsPage /> },
        { path: 'blog', element: <BlogPage /> },
        { path: 'settings', element: <SettingPage /> },
        { path: 'supports', element: <SupportsPage /> },
        { path: 'birth', element: <BirthdayPage /> },
        { path: 'activity/:userId', element: <UserActivityPage /> },
      ],
    },

    {
      element: <SimpleLayout />,
      children: [
        { element: <Navigate to="/login" />, index: true },
        { path: '404', element: <Page404 /> },
        { path: '*', element: <Navigate to="/404" /> },
      ],
    },
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },
  ]);
  return <Suspense fallback={<RouteFallback />}>{routes}</Suspense>;
}